import React, { useEffect, useState } from 'react';
import {
    AlertCircle, Check, Eye, EyeOff, Key, Lock as LockIcon,
    Shield, Fingerprint, ShieldAlert
} from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { Button } from '@/components/common/ui/Button';
import { usePasswordChange } from '@/hooks/auth/usePasswordChange';
import { VerificationModal } from '@/components/features/settings/VerificationModal';
import { apiFetch } from '@/lib/api/api';

const PasswordSettings: React.FC = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // --- MFA State (Preserved) ---
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [mfaLoading, setMfaLoading] = useState(false);

    const {
        changePassword,
        isLoading,
        verificationState,
        handleVerification,
        handleVerificationClose
    } = usePasswordChange();

    // --- Load current MFA state (Preserved) ---
    useEffect(() => {
        const loadMFAStatus = async () => {
            try {
                const res = await apiFetch('/mfa/status/');
                if (res.ok) {
                    const data = await res.json();
                    setMfaEnabled(data.mfa_enabled);
                }
            } catch (err) {
                console.error('Failed to load MFA status', err);
            }
        };
        loadMFAStatus();
    }, []);

    // --- Toggle MFA (Preserved) ---
    const handleToggleMFA = async (enabled: boolean) => {
        setMfaLoading(true);
        try {
            const res = await apiFetch('/mfa/toggle/', {
                method: 'POST',
                body: JSON.stringify({ enabled })
            });
            if (!res.ok) throw new Error('Toggle failed');
            const data = await res.json();
            setMfaEnabled(data.mfa_enabled);
        } catch (err) {
            console.error('Failed to toggle MFA:', err);
        } finally {
            setMfaLoading(false);
        }
    };

    const validatePassword = (password: string) => ({
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });

    const passwordRequirements = validatePassword(newPassword);
    const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

    const getPasswordStrength = (password: string) => {
        if (!password) return { width: '0%', color: 'bg-[#1A1D24]' };
        const checks = Object.values(validatePassword(password));
        const strength = checks.filter(Boolean).length;
        const percentage = (strength / checks.length) * 100;
        const colors = { 1: 'bg-[#FF3333]', 2: 'bg-orange-500', 3: 'bg-yellow-500', 4: 'bg-[#50A970]', 5: 'bg-[#00FFFF]' };
        return { width: `${percentage}%`, color: colors[strength as keyof typeof colors] || 'bg-[#1A1D24]' };
    };

    const handlePasswordChange = async () => {
        const newErrors: Record<string, string> = {};
        if (!currentPassword) newErrors.current = 'Current password is required';
        if (!newPassword) {
            newErrors.new = 'New password is required';
        } else if (!Object.values(passwordRequirements).every(Boolean)) {
            newErrors.new = 'Password does not meet requirements';
        }
        if (!confirmPassword) {
            newErrors.confirm = 'Please confirm your password';
        } else if (!passwordsMatch) {
            newErrors.confirm = 'Passwords do not match';
        }

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const result = await changePassword(currentPassword, newPassword);
            if (result.success) {
                setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setErrors({});
            } else if (result.error && result.error !== 'Password change cancelled') {
                if (result.error.includes('Current password is incorrect')) {
                    setErrors({ current: result.error });
                } else if (result.error.includes('does not meet requirements')) {
                    setErrors({ new: result.error });
                } else {
                    setErrors({ general: result.error });
                }
            }
        }
    };

    const strength = getPasswordStrength(newPassword);

    return (
        <div className="p-8 space-y-10 font-mono">

            {/* MFA SECTION (Upgraded UI, Preserved Logic) */}
            <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#A855F7] shadow-[0_0_15px_#A855F7]"></div>
                <div className="flex items-center gap-3 mb-4">
                    <Fingerprint className="h-5 w-5 text-[#A855F7]" />
                    <h4 className="text-[12px] font-bold text-white uppercase tracking-[0.2em]">Multi-Factor Authentication</h4>
                </div>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <p className="text-[11px] text-slate-500 uppercase leading-relaxed max-w-xl">
                        When enabled, you will receive a one-time verification code via email every time you log in.
                    </p>

                    <div className="flex items-center gap-4 bg-black border border-[#1A1D24] p-2 rounded-lg pr-4">
                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded ${mfaEnabled ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-600 bg-slate-900/50'}`}>
                            {mfaEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <Button
                            variant={mfaEnabled ? 'danger' : 'primary'}
                            size="sm"
                            className="h-8 text-[9px] px-4 font-black"
                            onClick={() => handleToggleMFA(!mfaEnabled)}
                            loading={mfaLoading}
                        >
                            {mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* PASSWORD SECTION */}
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <Key className="h-5 w-5 text-[#00FFFF]" />
                    <h3 className="text-[14px] font-bold text-white uppercase tracking-[0.2em]">Password Settings</h3>
                </div>

                {errors.general && (
                    <div className="p-4 bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-lg flex items-center gap-3 animate-in fade-in">
                        <ShieldAlert className="h-4 w-4 text-[#FF3333]" />
                        <span className="text-[11px] text-[#FF3333] font-bold uppercase tracking-widest">{errors.general}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div className="space-y-6">
                        <div className="space-y-4">
                            {/* Current Password Input */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Current Password</label>
                                <Input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => {
                                        setCurrentPassword(e.target.value);
                                        if (errors.current) setErrors(prev => ({ ...prev, current: '' }));
                                    }}
                                    className="bg-black border-[#1A1D24] text-[#00FFFF] h-11 text-[13px] focus:border-[#00FFFF]"
                                    placeholder="Enter current password"
                                    leftIcon={<LockIcon className="h-4 w-4 text-slate-700" />}
                                    rightIcon={
                                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="text-slate-600 hover:text-white">
                                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    }
                                    error={errors.current}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* New Password Input */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">New Password</label>
                                <Input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        if (errors.new) setErrors(prev => ({ ...prev, new: '' }));
                                    }}
                                    className="bg-black border-[#1A1D24] text-[#00FFFF] h-11 text-[13px] focus:border-[#00FFFF]"
                                    placeholder="Enter new password"
                                    leftIcon={<Key className="h-4 w-4 text-slate-700" />}
                                    rightIcon={
                                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="text-slate-600 hover:text-white">
                                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    }
                                    error={errors.new}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Confirm Password Input */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Confirm New Password</label>
                                <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        if (errors.confirm) setErrors(prev => ({ ...prev, confirm: '' }));
                                    }}
                                    className="bg-black border-[#1A1D24] text-[#00FFFF] h-11 text-[13px] focus:border-[#00FFFF]"
                                    placeholder="Confirm new password"
                                    leftIcon={<LockIcon className="h-4 w-4 text-slate-700" />}
                                    rightIcon={
                                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-slate-600 hover:text-white">
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    }
                                    error={errors.confirm}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {confirmPassword && passwordsMatch && (
                            <div className="flex items-center gap-2 text-[10px] text-[#50A970] font-bold uppercase tracking-widest bg-[#50A970]/5 p-2 rounded-lg border border-[#50A970]/20">
                                <Check className="h-3.5 w-3.5" /> Passwords match
                            </div>
                        )}
                    </div>

                    {/* Requirements & Strength Panel */}
                    <div className="space-y-6 bg-[#050505] border border-[#1A1D24] p-6 rounded-xl shadow-inner">
                        <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest border-b border-[#1A1D24] pb-3 mb-4">Password Requirements</h4>

                        <div className="space-y-3">
                            {[
                                { key: 'length', label: 'At least 8 characters' },
                                { key: 'uppercase', label: 'One uppercase letter' },
                                { key: 'lowercase', label: 'One lowercase letter' },
                                { key: 'number', label: 'One number' },
                                { key: 'special', label: 'One special character' }
                            ].map((req) => (
                                <div key={req.key} className="flex items-center justify-between group">
                                    <span className={`text-[10px] uppercase tracking-widest transition-colors ${passwordRequirements[req.key as keyof typeof passwordRequirements] ? 'text-green-400' : 'text-slate-600'}`}>
                                        {req.label}
                                    </span>
                                    {passwordRequirements[req.key as keyof typeof passwordRequirements] ? (
                                        <Check className="h-3.5 w-3.5 text-[#50A970]" />
                                    ) : (
                                        <AlertCircle className="h-3.5 w-3.5 text-red-900/50" />
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="space-y-2 pt-4 border-t border-[#1A1D24] mt-4">
                            <div className="flex justify-between text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                                <span>Strength Analysis</span>
                                <span className={strength.color.replace('bg-', 'text-')}>{strength.width === '100%' ? 'Optimal' : 'Checking...'}</span>
                            </div>
                            <div className="h-1.5 w-full bg-black border border-[#1A1D24] rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${strength.color} transition-all duration-500`}
                                    style={{ width: strength.width }}
                                />
                            </div>
                        </div>

                        <div className="bg-[#00FFFF]/5 border border-[#00FFFF]/20 rounded-lg p-4 flex gap-3 mt-6">
                            <Shield className="h-4 w-4 text-[#00FFFF] flex-shrink-0 mt-0.5" />
                            <div className="text-[10px] text-slate-500 uppercase leading-relaxed">
                                <p className="font-bold text-slate-300">Security Notice</p>
                                <p className="mt-1">You may be asked to verify your identity before commit. This helps keep your account secure.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-8 border-t border-[#1A1D24] flex justify-end">
                    <Button
                        onClick={handlePasswordChange}
                        loading={isLoading}
                        className="bg-[#00FFFF] text-black font-black px-10 h-12 text-[11px] tracking-[0.2em] shadow-[0_0_20px_rgba(0,255,255,0.3)] hover:scale-105 active:scale-95"
                        disabled={
                            !currentPassword || !newPassword || !confirmPassword ||
                            !passwordsMatch || !Object.values(passwordRequirements).every(Boolean)
                        }
                    >
                        {isLoading ? 'Updating Password...' : 'Update Password'}
                    </Button>
                </div>
            </div>

            <VerificationModal
                isOpen={verificationState.isOpen}
                onClose={handleVerificationClose}
                onVerify={handleVerification}
                isLoading={verificationState.isLoading}
                error={verificationState.error}
                level={verificationState.level}
            />
        </div>
    );
};

export default PasswordSettings;